import React from 'react';
import useAnalytics from 'helpers/useAnalytics';
import { useSelector } from 'react-redux';
import getHostName from 'helpers/getHostName';
import defaultNavigationItems from './helpers/defaultNavigationItems';
import makeNavigationItems from './helpers/makeNavigationItems';
import * as Types from './types';
import classnames from 'classnames';

// Components
import { Link } from 'react-router-dom';

import SiftLogo from '@sift/skipper/components/SiftLogo';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';

import NavListItemBase from './molecules/NavListItemBase';
import ListItem from './molecules/ListItem';
import ListItemText from './molecules/ListItemText';
import ListItemIcon from './molecules/ListItemIcon';
import InternalLinkNavItem from './InternalLinkNavItem';
import routesToNavigationItems from './helpers/routesToNavigationItems';
import SupportLinks from 'components/AppSidebar/SupportLinks';

import * as Routes from 'components/Router/routes';

// Styles
import { makeStyles } from '@material-ui/core/styles';
import useTheme from '@material-ui/core/styles/useTheme';

interface Props {
  dispatch: any;
}

function AppSidebar({ dispatch, ...other }: Props) {
  const { palette } = useTheme();
  const classes = useStyles(other);
  const analytics = useAnalytics();
  const navigationItems = makeNavigationItems(defaultNavigationItems());
  const clientSubdomain = useSelector((state: any) => state.client.subdomain);
  const hostName = getHostName();
  const directoryUrl = `https://${clientSubdomain}.${hostName}.com`;

  const handleReturnToSift = () => {
    analytics.track('Return to Sift Clicked', {
      category: 'AD - Navigation',
    });
  };

  return (
    <Drawer variant="permanent" {...other} classes={{ paper: classes.root }}>
      <List disablePadding className={classes.list}>
        <div>
          <Link to="/admin" aria-label="link-to-home-page">
            <ListItem className={classes.logoListItem}>
              <SiftLogo height={24} backgroundColor={palette.primary.main} />
            </ListItem>
          </Link>
          <a href={directoryUrl} onClick={handleReturnToSift}>
            <NavListItemBase>
              <ListItemIcon>
                <ExitToAppIcon className={classes.backToAppIcon} />
              </ListItemIcon>
              <ListItemText>Return to Sift</ListItemText>
            </NavListItemBase>
          </a>
          <Divider className={classes.divider} />
          <InternalLinkNavItem item={routesToNavigationItems([Routes.Home])[0]} />
          {// LEVEL ONE
          navigationItems.map((item, index) => (
            <div key={`${index}-${item.route ? item.route.title : 'section'}`}>
              {// LEVEL TWO
              item.children &&
                item.children.map((childItem: Types.InternalLinkNavItem) => (
                  <InternalLinkNavItem
                    item={childItem}
                    key={`${index}-${childItem.route ? childItem.route.title : 'item'}`}
                  />
                ))}
              {index === 0 && (
                <Divider className={classnames(classes.divider, classes.marginTop)} />
              )}
            </div>
          ))}
        </div>
        <div className={classes.support}>
          <SupportLinks />
        </div>
      </List>
    </Drawer>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    overflowX: 'hidden',
  },
  list: {
    width: theme.overrides.AppSidebar.width,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  logoListItem: {
    paddingTop: theme.spacing(2),
    minHeight: 68,
  },
  backToAppIcon: {
    transform: 'rotate(180deg)',
  },
  divider: {
    backgroundColor: theme.colors.navDivider,
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(2.25),
    marginBottom: theme.spacing(2.25),
  },
  marginTop: {
    marginTop: theme.spacing(2.25),
  },
  support: {
    marginBottom: theme.spacing(2.25),
  },
}));

export default AppSidebar;
